import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { showConfetti } from "../../../utils/confetti";
import { AiInterviewerFragment } from "../../graphql";
import { useIsScreenOrgAtsConnected } from "../../hooks/useIsScreenOrgAtsConnected";
import { pickTabIndex, TAB_INDICES } from "../AiInterviewer/useInterviewerTabs";
import { useAgenticIntakeEnabled } from "./AgenticIntake/useAgenticIntakeEnabled";
import { useFlexIntakeEnabled } from "./FlexIntake/useFlexIntakeEnabled";

// Standard wizard steps (with ATS integration)
export const WIZARD_STEPS = {
  JD: 0,
  GUIDE: 1,
  SETTINGS: 2,
  DONE: 3,
} as const;

// ATS-less wizard steps (includes Notifications step)
export const WIZARD_STEPS_ATS_LESS = {
  JD: 0,
  GUIDE: 1,
  SETTINGS: 2,
  NOTIFICATIONS: 3,
  DONE: 4,
} as const;

// Agentic intake steps (5-phase: Start, Training, Guide, Customization, Done)
export const WIZARD_STEPS_AGENTIC = {
  START: 0,
  TRAINING: 1,
  GUIDE: 2,
  CUSTOMIZATION: 3,
  DONE: 4,
} as const;

export type WizardStep = {
  label: string;
  header: string;
  description: string;
  onNext?: () => void;
  onBack?: () => void;
};

/** Allows a child component to override the wizard banner display within a single wizard step */
export type SubStepDisplay = {
  header: string;
  description: string;
  nextLabel?: string;
  onNext?: () => void;
  onBack?: () => void;
  stepperTitle?: string;
};

export type WizardHeaderProps = {
  aiInterviewer: AiInterviewerFragment;
  saving: boolean;
  isDirty: boolean;
  isAgentic: boolean;
};

export interface AiInterviewerWizardContextType {
  steps: WizardStep[];
  currentStep: number;
  wizardOn: boolean;
  setWizardOn?: (v: boolean) => void;
  registerVersionFormSubmit?: (fn: (callback?: () => void) => void) => void;
  submitVersionForm?: (callback?: () => void) => void;
  registerSetActiveTab?: (fn: (tabIdx: number) => void) => void;
  readOnly: boolean;
  setReadOnly?: (value: boolean) => void;
  subStepDisplay: SubStepDisplay | null;
  setSubStepDisplay?: (display: SubStepDisplay | null) => void;
  /** Agentic stepper visual state (0-4 for AgenticIntakeVerticalStepper) */
  agenticStepIndex: number;
  agenticSubStepIndex: number;
  setAgenticStepIndex?: (step: number, subStep?: number) => void;
  stepperJobTitle: string;
  setStepperJobTitle?: (title: string) => void;
  /** Header data lifted from AiInterviewer so the page can render it full-width */
  wizardHeaderProps: WizardHeaderProps | null;
  setWizardHeaderProps?: (props: WizardHeaderProps | null) => void;
  /** Whether the Guide Assistant panel is currently visible */
  guideAssistantVisible: boolean;
  setGuideAssistantVisible?: (visible: boolean) => void;
}

export const AiInterviewerWizardContext =
  React.createContext<AiInterviewerWizardContextType>({
    steps: [],
    currentStep: 0,
    wizardOn: false,
    setWizardOn: undefined,
    registerVersionFormSubmit: undefined,
    submitVersionForm: undefined,
    registerSetActiveTab: undefined,
    readOnly: false,
    setReadOnly: undefined,
    subStepDisplay: null,
    setSubStepDisplay: undefined,
    agenticStepIndex: 0,
    agenticSubStepIndex: 0,
    setAgenticStepIndex: undefined,
    stepperJobTitle: "",
    setStepperJobTitle: undefined,
    wizardHeaderProps: null,
    setWizardHeaderProps: undefined,
    guideAssistantVisible: false,
    setGuideAssistantVisible: undefined,
  });

type StepBuilderArgs = {
  isAtsLess: boolean;
  submitVersionForm: (callback?: () => void) => void;
  setActiveTab: (tabIdx: number) => void;
  navigate: (path: string) => void;
};

type AgenticStepBuilderArgs = StepBuilderArgs & {
  getAgenticSubStepIndex: () => number;
  setWizardOn: (v: boolean) => void;
};

function buildClassicSteps({
  isAtsLess,
  submitVersionForm,
  setActiveTab,
  navigate,
}: StepBuilderArgs): WizardStep[] {
  const steps: WizardStep[] = [
    {
      label: "Set up",
      header: "Setup AI interviewer",
      description: "Provide some basic information about the AI interviewer.",
    },
    {
      label: "Guide",
      header: "Review interview guide",
      description:
        "Review and edit the generated interview questions and evaluation criteria as needed.",
      onNext: () => setActiveTab(TAB_INDICES.SETTINGS),
    },
    {
      label: "Options",
      header: "Customize AI interviewer",
      description:
        "Optionally add information to help the AI interviewer answer candidate questions, set introduction and closing messages, and adjust duration and interview type.",
      onNext: () => {
        if (isAtsLess) {
          submitVersionForm(() => setActiveTab(TAB_INDICES.NOTIFICATIONS));
        } else {
          submitVersionForm(() => setActiveTab(TAB_INDICES.CANDIDATES));
        }
      },
      onBack: () => setActiveTab(TAB_INDICES.GUIDE),
    },
  ];

  if (isAtsLess) {
    steps.push({
      label: "Notifications",
      header: "Configure notifications",
      description:
        "Set up email notifications to receive alerts when candidates complete their interviews.",
      onNext: () =>
        submitVersionForm(() => setActiveTab(TAB_INDICES.CANDIDATES)),
      onBack: () => setActiveTab(TAB_INDICES.SETTINGS),
    });
  }

  steps.push({
    label: "Done",
    header: "View candidate results",
    description:
      "All done! Your new AI interviewer is complete. When you're ready, publish it and invite candidates. Their results will be available here.",
    onNext: () => navigate("/ai-interviewers"),
    onBack: () => {
      if (isAtsLess) {
        setActiveTab(TAB_INDICES.NOTIFICATIONS);
      } else {
        setActiveTab(TAB_INDICES.SETTINGS);
      }
    },
  });

  return steps;
}

function buildAgenticIntakeSteps({
  isAtsLess,
  submitVersionForm,
  setActiveTab,
  navigate,
  getAgenticSubStepIndex,
  setWizardOn,
}: AgenticStepBuilderArgs): WizardStep[] {
  return [
    {
      label: "Start",
      header: "Set up AI interviewer",
      description: "Configure your AI interviewer and interview guide.",
    },
    {
      label: "Training",
      header: "Provide materials",
      description: "Add materials to help make your interviewer smarter.",
    },
    {
      label: "Guide",
      header: "Review interview guide",
      description: "Review your interview questions and evaluation criteria.",
      onNext: () => submitVersionForm(() => setActiveTab(TAB_INDICES.SETTINGS)),
      onBack: undefined,
    },
    {
      label: "Customization",
      header: "Customize AI interviewer",
      description:
        "Add company info for candidate questions, set interview intro/outro scripts, and choose interviewer settings.",
      onNext: () => {
        const subStep = getAgenticSubStepIndex();
        if (subStep === 0) {
          // Options → Notifications
          submitVersionForm(() => setActiveTab(TAB_INDICES.NOTIFICATIONS));
        } else if (subStep === 1) {
          // Notifications → Test Interviews
          submitVersionForm(() => setActiveTab(TAB_INDICES.TEST_INTERVIEW));
        } else {
          // Test Interviews → exit wizard, go to candidates, confetti
          submitVersionForm(() => {
            setWizardOn(false);
            setActiveTab(TAB_INDICES.CANDIDATES);
            showConfetti();
          });
        }
      },
      onBack: () => setActiveTab(TAB_INDICES.GUIDE),
    },
    {
      label: "Done",
      header: "View candidate results",
      description:
        "All done! Your AI interviewer is ready. Publish it and invite candidates.",
      onNext: () => navigate("/ai-interviewers"),
      onBack: () => {
        if (isAtsLess) {
          setActiveTab(TAB_INDICES.NOTIFICATIONS);
        } else {
          setActiveTab(TAB_INDICES.SETTINGS);
        }
      },
    },
  ];
}

function buildFlexIntakeSteps({
  isAtsLess,
  submitVersionForm,
  setActiveTab,
  navigate,
}: StepBuilderArgs): WizardStep[] {
  const steps: WizardStep[] = [
    {
      label: "Setup",
      header: "Set up AI interviewer",
      description: "Configure your AI interviewer and interview guide.",
    },
    {
      label: "Guide",
      header: "Review interview guide",
      description:
        "Review and edit the generated interview questions and evaluation criteria as needed.",
      onNext: () => setActiveTab(TAB_INDICES.SETTINGS),
    },
    {
      label: "Customization",
      header: "Customize AI interviewer",
      description:
        "Add context, set messages, and adjust duration and interview type.",
      onNext: () => {
        if (isAtsLess) {
          submitVersionForm(() => setActiveTab(TAB_INDICES.NOTIFICATIONS));
        } else {
          submitVersionForm(() => setActiveTab(TAB_INDICES.CANDIDATES));
        }
      },
      onBack: () => setActiveTab(TAB_INDICES.GUIDE),
    },
  ];

  if (isAtsLess) {
    steps.push({
      label: "Notifications",
      header: "Configure notifications",
      description:
        "Set up email notifications to receive alerts when candidates complete their interviews.",
      onNext: () =>
        submitVersionForm(() => setActiveTab(TAB_INDICES.CANDIDATES)),
      onBack: () => setActiveTab(TAB_INDICES.SETTINGS),
    });
  }

  steps.push({
    label: "Done",
    header: "View candidate results",
    description:
      "All done! Your AI interviewer is ready. Publish it and invite candidates.",
    onNext: () => navigate("/ai-interviewers"),
    onBack: () => {
      if (isAtsLess) {
        setActiveTab(TAB_INDICES.NOTIFICATIONS);
      } else {
        setActiveTab(TAB_INDICES.SETTINGS);
      }
    },
  });

  return steps;
}

export const AiInterviewerWizardProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAtsLess = !useIsScreenOrgAtsConnected();
  const agenticIntakeEnabled = useAgenticIntakeEnabled();
  const flexIntakeEnabled = useFlexIntakeEnabled();
  const [wizardOn, setWizardOn] = useState(
    new URLSearchParams(location.search).get("wizard") === "true"
  );
  const [currentStep, setCurrentStep] = useState(0);
  const [readOnly, setReadOnly] = useState(false); // disables wizard navigation
  const [subStepDisplay, setSubStepDisplay] = useState<SubStepDisplay | null>(
    null
  );

  const [agenticStepIndex, setAgenticStepIndexRaw] = useState(0);
  const [agenticSubStepIndex, setAgenticSubStepIndexRaw] = useState(0);
  const agenticSubStepIndexRef = useRef(0);
  const [stepperJobTitle, setStepperJobTitle] = useState("");
  const [wizardHeaderProps, setWizardHeaderProps] =
    useState<WizardHeaderProps | null>(null);
  const [guideAssistantVisible, setGuideAssistantVisible] = useState(false);
  const setAgenticStepIndex = useCallback((step: number, subStep = 0) => {
    setAgenticStepIndexRaw(step);
    setAgenticSubStepIndexRaw(subStep);
    agenticSubStepIndexRef.current = subStep;
  }, []);

  // Use ATS-less steps if org is ATS-less
  const wizardSteps = isAtsLess ? WIZARD_STEPS_ATS_LESS : WIZARD_STEPS;

  // Set step based on URL
  useEffect(() => {
    if (!wizardOn) return;
    const tabIdx = pickTabIndex(location.pathname);

    if (agenticIntakeEnabled) {
      if (
        location.pathname.endsWith("/new") ||
        location.pathname.endsWith("/train")
      ) {
        // Create view manages its own sub-phase (Start/Training/Generating)
        setCurrentStep(WIZARD_STEPS_AGENTIC.START);
      } else if (tabIdx === TAB_INDICES.GUIDE) {
        setCurrentStep(WIZARD_STEPS_AGENTIC.GUIDE);
      } else if (tabIdx === TAB_INDICES.CANDIDATES) {
        setCurrentStep(WIZARD_STEPS_AGENTIC.DONE);
      } else {
        setCurrentStep(WIZARD_STEPS_AGENTIC.CUSTOMIZATION);
      }
    } else if (
      location.pathname.endsWith("/new") ||
      location.pathname.endsWith("/train")
    ) {
      setCurrentStep(wizardSteps.JD);
    } else if (tabIdx === TAB_INDICES.CANDIDATES) {
      setCurrentStep(wizardSteps.DONE);
    } else if (tabIdx === TAB_INDICES.GUIDE) {
      setCurrentStep(wizardSteps.GUIDE);
    } else if (tabIdx === TAB_INDICES.NOTIFICATIONS) {
      if (isAtsLess) {
        setCurrentStep(WIZARD_STEPS_ATS_LESS.NOTIFICATIONS);
      } else {
        setCurrentStep(wizardSteps.SETTINGS);
      }
    } else {
      setCurrentStep(wizardSteps.SETTINGS);
    }
  }, [
    location.pathname,
    wizardOn,
    isAtsLess,
    wizardSteps,
    agenticIntakeEnabled,
  ]);

  // Imperative tab control
  const setActiveTabRef = useRef<((tabIdx: number) => void) | null>(null);
  const registerSetActiveTab = useCallback((fn: (tabIdx: number) => void) => {
    setActiveTabRef.current = fn;
  }, []);
  const setActiveTab = useCallback((tabIdx: number) => {
    if (setActiveTabRef.current) setActiveTabRef.current(tabIdx);
  }, []);

  // Imperative version form submit control
  const versionFormSubmitRef = useRef<((callback?: () => void) => void) | null>(
    null
  );
  const registerVersionFormSubmit = useCallback(
    (fn: (callback?: () => void) => void) => {
      versionFormSubmitRef.current = fn;
    },
    []
  );
  const submitVersionForm = useCallback((callback?: () => void) => {
    if (versionFormSubmitRef.current) versionFormSubmitRef.current(callback);
  }, []);

  const steps: WizardStep[] = useMemo(() => {
    if (agenticIntakeEnabled) {
      return buildAgenticIntakeSteps({
        isAtsLess,
        submitVersionForm,
        setActiveTab,
        navigate,
        getAgenticSubStepIndex: () => agenticSubStepIndexRef.current,
        setWizardOn,
      });
    }
    if (flexIntakeEnabled) {
      return buildFlexIntakeSteps({
        isAtsLess,
        submitVersionForm,
        setActiveTab,
        navigate,
      });
    }
    return buildClassicSteps({
      isAtsLess,
      submitVersionForm,
      setActiveTab,
      navigate,
    });
  }, [
    agenticIntakeEnabled,
    flexIntakeEnabled,
    submitVersionForm,
    setActiveTab,
    navigate,
    isAtsLess,
  ]);

  const contextValue = useMemo(
    () => ({
      steps,
      currentStep,
      wizardOn,
      setWizardOn,
      registerVersionFormSubmit,
      submitVersionForm,
      registerSetActiveTab,
      readOnly,
      setReadOnly,
      subStepDisplay,
      setSubStepDisplay,
      agenticStepIndex,
      agenticSubStepIndex,
      setAgenticStepIndex,
      stepperJobTitle,
      setStepperJobTitle,
      wizardHeaderProps,
      setWizardHeaderProps,
      guideAssistantVisible,
      setGuideAssistantVisible,
    }),
    [
      steps,
      currentStep,
      wizardOn,
      setWizardOn,
      registerVersionFormSubmit,
      submitVersionForm,
      registerSetActiveTab,
      readOnly,
      setReadOnly,
      subStepDisplay,
      agenticStepIndex,
      agenticSubStepIndex,
      setAgenticStepIndex,
      stepperJobTitle,
      wizardHeaderProps,
      guideAssistantVisible,
    ]
  );

  return (
    <AiInterviewerWizardContext.Provider value={contextValue}>
      {children}
    </AiInterviewerWizardContext.Provider>
  );
};

// Wrapper for use in routes
export const AiInterviewerWizardProviderWrapper: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => (
  <AiInterviewerWizardProvider>{children}</AiInterviewerWizardProvider>
);
